<script setup>
import {Head, Link} from "@inertiajs/vue3";

import PageLayout from "@/Layouts/PageLayout.vue";
import GeneralLayout from "@/Layouts/GeneralLayout.vue";
import PracticeThisCTA from "@/Components/Marketing/PracticeThisCTA.vue";
import {BRAND_NAME, frequently_asked_questions, introductionQuestions, sampleSpeakingExamsFAQ} from "@/constants.js";
import FAQ from "@/Components/FAQ.vue";

const MetaData = {
    title: 'Sample IELTS Speaking Tests - Free IELTS Speaking Practice',
    description: 'Get free IELTS speaking practice at {{BRAND_NAME}}! Ace your exam with our sample speaking tests. Improve fluency & confidence now!'
}
const props = defineProps({
    questionSets: {
        type: Array,
        default: () => []
    },
    title: {
        type: String,
        default: ''
    },
    description: {
        type: String,
        default: ''
    }
})

</script>

<template>
    <Head>
        <title>{{ MetaData.title }}</title>
        <meta name="description" :content="MetaData.description">
    </Head>
    <GeneralLayout>
        <template #header>

        </template>


        <section class="max-w-6xl mx-auto p-5">
            <h1 class="text-3xl font-bold text-center mb-6">{{ MetaData.title }}</h1>
            <p class="mb-5">
                Are you preparing for the IELTS speaking exam and searching for potent practice resources? {{
                    BRAND_NAME
                }} offers invaluable insights, practice methodologies, and access to free IELTS speaking practice tests
                to boost your confidence and performance and help you conquer the IELTS speaking exam.
            </p>

            <h2 class="text-2xl font-semibold mb-4">Championing the IELTS Speaking Exam</h2>
            <p class="mb-5">
                The IELTS speaking assessment requires one to attempt parts 1, 2, and 3. Each section evaluates your
                capacity to communicate effectively in English. Excelling in these evaluations is essential to attain
                your desired IELTS score and refine your overall English-speaking dexterity.
            </p>

            <h2 class="text-2xl font-semibold mb-4">Why Opt for {{ BRAND_NAME }} for IELTS Speaking Exam Readiness?</h2>
            <ul class="list-disc pl-6 mb-5 space-y-2">
                <li><strong>Diverse Sample Speaking Exams:</strong> {{ BRAND_NAME }} provides a rich collection of
                    sample speaking exams covering various topics and scenarios. This diversity ensures you can practice
                    diverse question types and improve your ability to respond effectively.
                </li>
                <li><strong>Expert Guidance:</strong> We offer sagacious insights, tips, and strategies for tackling
                    each aspect of the IELTS speaking exam. Our counsel assists you in comprehending the precise
                    requirements and expectations of the examination.
                </li>
                <li><strong>Structured Practice:</strong> {{ BRAND_NAME }} offers structured practice exercises for each
                    part of the speaking exams. These exercises help acclimate you to the format and time constraints of
                    the actual examination.
                </li>
                <li><strong>Sample Answers:</strong> Explore sample answers epitomizing effective responses to various
                    speaking examination queries. Analyzing these answers enhances your understanding of how to
                    formulate your responses.
                </li>
                <li><strong>Realistic Test Simulation:</strong> Practice under timed conditions to replicate the
                    challenges of the actual IELTS speaking exams. This preparation ensures that you can manage your
                    time effectively during the test.
                </li>
            </ul>

            <h2 class="text-2xl font-semibold mb-4">Maximizing the Potential of {{ BRAND_NAME }}'s Sample Speaking
                Exams</h2>
            <p class="mb-5"><strong>Select a Section</strong>: Select a section from Part 1, 2, or 3 that you wish to
                practice.</p>
            <p class="mb-5"><strong>Practice</strong>: Engage in practice sessions that closely resemble the actual
                speaking exams. Pay close attention to fluency of speech, cohesiveness, and vocabulary usage.</p>
            <p class="mb-5"><strong>Self-Assess</strong>: After each session, assess your performance in light of the
                guidelines and sample answers at your disposal. Take notes on areas that warrant improvement.</p>
            <p class="mb-5"><strong>Repeat and Improve</strong>: Practice different sample speaking exams to enhance
                your proficiency. Focus on refining your responses and addressing any feedback you receive.</p>
            <p class="mb-5"><strong>Join the {{ BRAND_NAME }} Community</strong>: Connect with fellow IELTS candidates
                on our platform. Share your experiences, seek advice, and collaborate with others to enhance your
                speaking skills collectively.</p>

            <h2 class="text-xl font-bold mb-4">Discover Your Key to IELTS Success - Try {{ BRAND_NAME }} Today</h2>
            <p class="mb-8">
                Are you ready to master the IELTS speaking exams and achieve your desired score? Sign up with
                {{ BRAND_NAME }} today and leverage our free IELTS speaking practice tests and resources to improve your
                performance and succeed in the IELTS exam. The world is waiting to hear your expertise – make it happen
                with
                {{ BRAND_NAME }}!

            </p>
            <PracticeThisCTA page-type="sample_exams_list" placement="bottom"/>

        </section>

        <div class="max-w-6xl mx-auto sm:py-2 md:py-8">
            <h2 class="text-2xl font-semibold text-center mb-4">
                IELTS Speaking Sample Exams
            </h2>
            <section v-for="questionSet in questionSets" class="p-4 bg-gray-50 my-4 dark:bg-gray-900">
                <h2 class="font-semibold">
                    Sample Exam {{ questionSet.id }} - {{ questionSet.title }}
                </h2>
                <section class="p-2 lg:p-4 my-3 shadow-sm rounded-lg">
                    <h3 class="font-semibold text-lg dark:text-white text-primary-600">Part 1</h3>
                    <div>
                        <div class="text-xl font-semibold mb-4">Introduction Questions</div>

                        <div v-for="introductionQuestion in introductionQuestions"
                             class="py-2 px-4 bg-gray-100 rounded-md mb-2">
                            {{ introductionQuestion }}
                        </div>

                        <div v-if="questionSet.topic1" class="mt-6">
                            <h3 class="font-semibold text-medium dark:text-white text-primary-600 mb-3">Lets start with
                                {{ questionSet.topic1.title }}</h3>
                            <div v-for="question in questionSet.topic1.questions"
                                 class="py-2 px-4 bg-gray-100 rounded-md mb-2">
                                <strong>{{ question.question }}</strong>
                                <!-- answer -->
                                <div class="p-2 my-3">
                                    <span class="font-medium">Sample Answer:</span> {{ question.answer }}
                                </div>

                            </div>


                        </div>

                        <div v-if="questionSet.topic2" class="mt-6">
                            <h3 class="font-semibold text-medium dark:text-white text-primary-600 mb-3">Lets start with
                                {{ questionSet.topic2.title }}</h3>
                            <div v-for="question in questionSet.topic2.questions"
                                 class="py-2 px-4 bg-gray-100 rounded-md mb-2">
                                <strong>{{ question.question }}</strong>
                                <!-- answer -->
                                <div class="p-2 my-3">
                                    <span class="font-medium">Sample Answer:</span> {{ question.answer }}
                                </div>

                            </div>
                        </div>

                        <div v-if="questionSet.topic3" class="mt-6">
                            <h3 class="font-semibold text-medium dark:text-white text-primary-600 mb-3">Lets start with
                                {{ questionSet.topic3.title }}</h3>
                            <div v-for="question in questionSet.topic3.questions"
                                 class="py-2 px-4 bg-gray-100 rounded-md mb-2">
                                <strong>{{ question.question }}</strong>
                                <!-- answer -->
                                <div class="p-2 my-3">
                                    <span class="font-medium">Sample Answer:</span> {{ question.answer }}
                                </div>

                            </div>
                        </div>
                    </div>
                </section>

                <section v-if="questionSet.cue_card" class="p-2 lg:p-4 my-3 shadow-sm rounded-lg">
                    <h2 class="font-semibold text-lg dark:text-white text-primary-600 py-2">Part 2</h2>

                    <p class="mt-6 bg-primary-100 p-4 rounded-lg">
                        Now, I'm going to give you a topic and I'd like you to talk about it for 1-2 minutes.
                        Before you talk you'll have one minute to think about what you are going to say and you can
                        make notes if you wish. Do you understand?
                    </p>

                    <section class="bg-gray-300 p-6 mt-6 rounded-lg">
                        <p class="font-semibold">{{ questionSet.cue_card.title }}</p>
                        <div class="font-medium my-2">You should say:</div>
                        <ul class="list-disc pl-5">
                            <li v-for="subquestion in questionSet.cue_card.sub_questions" class="mb-1">
                                {{ subquestion }}
                            </li>
                        </ul>

                        <div class="p-2 my-3 bg-gray-200">
                            <div class="font-medium">Sample Answer:</div>
                            <ul>
                                <li v-for="line in questionSet.cue_card.answer">
                                    • {{ line }}
                                </li>
                            </ul>
                        </div>
                    </section>
                </section>

                <section v-if="questionSet.cue_card.questions" class="p-2 lg:p-4 my-3 shadow-sm rounded-lg">
                    <h2 class="font-semibold text-lg dark:text-white text-primary-600 py-2">Part 3</h2>

                    <section class="mt-6">
                        <h3 class="font-semibold text-medium">
                            We've been talking about {{ questionSet.cue_card.title }}, and I'd now like to ask you
                            some questions related to this
                        </h3>
                        <ul class="list-disc pl-5 mt-3">
                            <li v-for="question in questionSet.cue_card.questions" class="mb-1">
                                <strong>{{ question.question }}</strong>
                                <div class="p-2 my-3">
                                    <span class="font-medium">Sample Answer:</span> {{ question.answer }}
                                </div>
                            </li>
                        </ul>
                    </section>
                </section>

            </section>
            <FAQ :faqItems="sampleSpeakingExamsFAQ"/>
        </div>
    </GeneralLayout>
</template>

<style scoped>

</style>
